// SEO landing page for legacy cultural-entity topics at /start/<slug>.
// Public JSON + optional auth; long-form body uses ToolPageCopy from meta.pageCopy.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Platform,
  Pressable,
  ActivityIndicator,
} from 'react-native';
import { Link, useLocalSearchParams, useRouter } from 'expo-router';
import { useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import useResponsiveness from '@/hooks/useResponsiveness';
import useRequireAuth from '@/hooks/useRequireAuth';
import useLazyInView from '@/hooks/useLazyInView';
import MetaHead from '@/components/MetaHead';
import CatalogDetailHero from '@/components/Catalog/CatalogDetailHero';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import TopicPageCopy from '@/components/Topics/TopicPageCopy';
import TopicTagChips from '@/components/Topics/TopicTagChips';
import TopicPageFeedback from '@/components/Topics/TopicPageFeedback';
import useTopicPageFeedback from '@/hooks/useTopicPageFeedback';
import ChatInput from '@/components/ChatInput/ChatInput';
import ChatInputContainer from '@/components/ChatInput/ChatInputContainer';
import AppDashboardFooter from '@/components/AppDashboardFooter';
import { topicApiSsr } from '@/lib/topicApi';
import { setTopicNavEntry, titleFromTopicSlug } from '@/lib/topicNavCache';
import socketManager from '@/lib/socket';
import { CHAT_INPUT_STYLE } from '@/constants/config';
import { hasAvailableCredits, openCreditsRecoveryModal } from '@/constants/utils/trial';
import {
  useAuthStore,
  useChatStore,
  useChatInputStore,
  useModalStore,
  useToastStore,
  useModelStore,
} from '@/store';
import { normalizeTopicPageCopy } from '@/utils/topic_page_copy';
import { buildTopicPageStructuredData } from '@/lib/topicStructuredData';
import { buildTopicSerpDescription, resolveTopicShareImage, snippetExcludeWebProps } from '@/utils/topic_seo';
import { colorForSlug, heroChromaForSlug, formatCount } from '@/utils/agent_catalog';
import { BORDER_RADIUS } from '@/constants/config';
import { Image } from 'expo-image';

const _staticTopicCache = {};
const FLOATING_INPUT_BOTTOM_PAD = 132;
const EMPTY_MODEL_IDS = Object.freeze([]);

function buildTopicExplorePrompt(topicName) {
  return `Tell me more about ${topicName}`;
}

function buildDefaultPromptPrefill(prompt) {
  return `tell me more about ${prompt}`;
}

function RelatedTopicCard({ row, styles, onPress }) {
  const { ref: avatarRef, inView } = useLazyInView();

  return (
    <Pressable
      style={styles.relatedCard}
      onPress={() => onPress(row)}
      accessibilityRole="button"
      accessibilityLabel={row.name}
    >
      <View ref={avatarRef}>
        {row.avatarPath && inView ? (
          <Image
            source={{ uri: row.avatarPath }}
            style={styles.relatedAvatar}
            contentFit="cover"
            priority="low"
            recyclingKey={String(row.id || row.slug)}
          />
        ) : (
          <View style={styles.relatedAvatar} />
        )}
      </View>
      <Text type="p" weight="bold" numberOfLines={2}>{row.name}</Text>
    </Pressable>
  );
}

export async function generateStaticParams() {
  // Rendered on demand (backend SSR meta + client SPA), not prerendered.
  // Returning [] keeps `expo export` fast and avoids hammering the API at
  // build time. /start/:slug SEO is injected at request time in
  // backend/routes/appRoutes.js; URLs are discovered via /json/topics/sitemap.xml.
  return [];
}

const createStyles = (colors, isXS, isSM, isMD, isXL, insetBottom, accent, heroChroma, cardWidth) => {
  const stacked = isSM;
  return StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.solid_color_primary },
    scroll: { flex: 1, width: '100%' },
    scrollContent: { paddingBottom: insetBottom + FLOATING_INPUT_BOTTOM_PAD + 24 },
    inner: {
      paddingHorizontal: isXS ? 16 : 24,
      paddingTop: 8,
      maxWidth: 1100,
      alignSelf: 'center',
      width: '100%',
      gap: 20,
    },
    contentSplit: {
      flexDirection: stacked ? 'column' : 'row',
      flexWrap: stacked ? 'nowrap' : 'wrap',
      alignItems: 'flex-start',
      gap: stacked ? 20 : 28,
      width: '100%',
    },
    pageCopyCol: {
      flex: 1,
      minWidth: 0,
      gap: 24,
    },
    promptsSidebar: {
      width: stacked ? '100%' : 420,
      flexShrink: 0,
      gap: 8,
      ...(Platform.OS === 'web' && !stacked ? { position: 'sticky', top: 12 } : null),
    },
    promptsSidebarCard: {
      gap: 8,
      ...(stacked
        ? {
          borderTopWidth: 1,
          borderTopColor: colors.border_color_secondary,
          paddingTop: 16,
        }
        : {
          borderLeftWidth: 1,
          borderLeftColor: colors.border_color_secondary,
          paddingLeft: 16,
        }),
    },
    fullBleedBand: { width: '100%' },
    loadingBox: { flex: 1, alignItems: 'center', justifyContent: 'center', gap: 12 },
    notFound: { alignItems: 'center', gap: 12, paddingVertical: 48 },
    promptList: { gap: 8 },
    promptRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_tertiary,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
    },
    promptRowHovered: {
      backgroundColor: `${accent}18`,
      borderColor: `${accent}40`,
    },
    relatedGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 10,
    },
    relatedCard: {
      width: cardWidth,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: 12,
      gap: 8,
      ...(Platform.OS === 'web' ? { cursor: 'pointer' } : null),
    },
    relatedAvatar: {
      width: 40,
      height: 40,
      borderRadius: 10,
      backgroundColor: `${accent}22`,
    },
    footerWrap: { paddingHorizontal: isXS ? 16 : 24, paddingTop: 24 },
    sectionTitle: { marginBottom: 8 },
    starterSectionTitle: { marginTop: 10, marginBottom: 8 },
    relatedSectionTitle: { marginTop: 15, marginBottom: 20 },
    tagsSectionTitle: { marginTop: 15, marginBottom: 16 },
    tagsSection: { gap: 4 },
    explainerCard: {
      marginTop: 5,
      borderRadius: BORDER_RADIUS.medium,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      padding: isXS ? 16 : 20,
      gap: 10,
    },
    floatingComposer: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      paddingHorizontal: isXS ? 12 : 20,
      paddingBottom: Math.max(insetBottom, 12),
      paddingTop: 8,
      backgroundColor: 'transparent',
    },
  });
};

export default function TopicStartPage() {
  const params = useLocalSearchParams();
  const slugRaw = params?.slug;
  const slug =
    typeof slugRaw === 'string'
      ? slugRaw
      : Array.isArray(slugRaw)
        ? slugRaw[0] ?? null
        : slugRaw ?? null;
  const slugNorm = slug ? String(slug).toLowerCase() : '';
  const composerContextId = slugNorm ? `topic-start-${slugNorm}` : 'topic-start';

  const router = useRouter();
  const navigation = useNavigation();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { isXS, isSM, isXL, isMD, showsScrollIndicator } = useResponsiveness();
  const insets = useSafeAreaInsets();
  const requireAuth = useRequireAuth();
  const userId = useAuthStore((s) => s.user?.id);
  const isPlus = useAuthStore((s) => s.user?.flags?.isPlus);
  const totalAvailableCredits = useAuthStore((s) => s.user?.credits?.totalAvailable ?? 0);
  const token = useAuthStore((s) => s.token);
  const isTokenExpired = useAuthStore((s) => s.isTokenExpired);
  const createConversation = useChatStore((s) => s.createConversation);
  const { addToast } = useToastStore((s) => s.actions);
  const { openModal } = useModalStore((s) => s.actions);
  const setInputContext = useChatInputStore((s) => s.setContext);
  const setText = useChatInputStore((s) => s.setText);
  const requestInputFocus = useChatInputStore((s) => s.requestInputFocus);
  const selectedModelIds = useChatInputStore(
    (s) => s.inputStates?.[composerContextId]?.selectedModelIds ?? EMPTY_MODEL_IDS,
  );
  const getModelById = useModelStore((s) => s.getModelById);
  const models = useModelStore((s) => s.models);
  const userDefaultTextModelId = useAuthStore((s) => {
    const raw = s.user?.defaultModel?.text;
    if (!raw) return null;
    return typeof raw === 'string' ? raw : raw.id || null;
  });
  const isAuthenticated = Boolean(token && !isTokenExpired?.());

  const [remoteTopic, setRemoteTopic] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [relatedTopicRows, setRelatedTopicRows] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [loadingRemote, setLoadingRemote] = useState(false);

  const staticTopic = slug
    ? (_staticTopicCache[slug] || _staticTopicCache[slugNorm])
    : null;

  useEffect(() => {
    let cancelled = false;
    if (!slug) return undefined;
    setRemoteTopic(null);
    setLoadingRemote(true);
    setLoadError(null);
    (async () => {
      try {
        const fetched = await topicApiSsr.getBySlug(slug);
        if (cancelled) return;
        if (fetched?.slug) {
          setRemoteTopic(fetched);
          setLoadError(null);
        } else {
          setRemoteTopic(null);
          setLoadError('notfound');
        }
      } catch (e) {
        if (!cancelled) {
          setRemoteTopic(null);
          setLoadError(e?.message || 'error');
        }
      } finally {
        if (!cancelled) setLoadingRemote(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const topic = useMemo(() => {
    if (remoteTopic && String(remoteTopic.slug || '').toLowerCase() === slugNorm) {
      return remoteTopic;
    }
    if (staticTopic && String(staticTopic.slug || '').toLowerCase() === slugNorm) {
      return staticTopic;
    }
    return null;
  }, [remoteTopic, staticTopic, slugNorm]);

  const resolvedComposerModel = useMemo(() => {
    const resolveModel = (id) => {
      if (!id) return null;
      return getModelById?.(id) || models?.[id] || null;
    };
    let m = null;
    if (selectedModelIds.length >= 1) {
      m = resolveModel(selectedModelIds[0]);
    } else if (userDefaultTextModelId) {
      m = resolveModel(userDefaultTextModelId);
    }
    if (!m) {
      m = Object.values(models || {}).find(
        (row) => String(row?.slug || '').toLowerCase() === 'arya',
      ) || null;
    }
    return {
      name: m?.name || 'Arya',
      logoPath: m?.logoPath || null,
    };
  }, [selectedModelIds, userDefaultTextModelId, models, getModelById]);

  const composerModelName = resolvedComposerModel.name;

  const chatPlaceholder = useMemo(() => {
    const topicName = topic?.name || titleFromTopicSlug(slug);
    return t('pages.topics.chatPlaceholder', { model: composerModelName, topic: topicName });
  }, [topic?.name, slug, composerModelName, t]);

  useEffect(() => {
    if (!composerContextId) return undefined;
    setInputContext(composerContextId);
    return undefined;
  }, [composerContextId, setInputContext]);

  useEffect(() => {
    if (topic?.slug && topic?.name && String(topic.slug).toLowerCase() === slugNorm) {
      setTopicNavEntry(topic.slug, topic.name);
    }
  }, [topic?.slug, topic?.name, slugNorm]);

  useLayoutEffect(() => {
    const displayName = (topic?.name && String(topic.slug || '').toLowerCase() === slugNorm)
      ? topic.name
      : titleFromTopicSlug(slug);
    navigation.setOptions({ headerTitle: displayName });
  }, [navigation, slug, slugNorm, topic?.name, topic?.slug]);

  const categorySlug = topic?.category?.slug || topic?.categorySlug || '';

  useEffect(() => {
    let cancelled = false;
    if (!slug || !topic?.id) {
      setRelatedTopicRows([]);
      return undefined;
    }
    (async () => {
      try {
        const list = await topicApiSsr.getRelatedBySlug(slug, { limit: 16 });
        if (!cancelled) {
          setRelatedTopicRows(list.filter((row) => row?.id && row.id !== topic.id));
        }
      } catch {
        if (!cancelled) setRelatedTopicRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, topic?.id]);

  const accent = useMemo(() => colorForSlug(categorySlug), [categorySlug]);
  const heroChroma = useMemo(() => heroChromaForSlug(categorySlug), [categorySlug]);
  const gridCols = isXS ? 1 : isMD ? 2 : isXL ? 4 : 3;
  const cardWidth = Platform.OS === 'web'
    ? `calc((100% - ${(gridCols - 1) * 10}px) / ${gridCols})`
    : `${100 / gridCols}%`;

  const styles = useMemo(
    () => createStyles(colors, isXS, isSM, isMD, isXL, insets.bottom, accent, heroChroma, cardWidth),
    [colors, isXS, isSM, isMD, isXL, insets.bottom, accent, heroChroma, cardWidth],
  );

  const pageCopyBlocks = useMemo(
    () => normalizeTopicPageCopy(topic?.meta?.pageCopy, topic?.name),
    [topic],
  );

  const prompts = useMemo(
    () => (Array.isArray(topic?.defaultPrompts) ? topic.defaultPrompts.filter(Boolean) : []),
    [topic],
  );

  const prefillChat = useCallback((promptText) => {
    if (!promptText || !composerContextId) return;
    setInputContext(composerContextId);
    setText(promptText);
    requestInputFocus(composerContextId);
  }, [composerContextId, setInputContext, setText, requestInputFocus]);

  const handlePageCopyQuestion = useCallback((question) => {
    const raw = String(question || '').replace(/\?+$/, '').trim();
    if (!raw) return;
    prefillChat(buildDefaultPromptPrefill(raw));
  }, [prefillChat]);

  const handlePageCopyContinue = useCallback((sectionTitle) => {
    const section = String(sectionTitle || '').replace(/\?+$/, '').trim();
    const name = topic?.name;
    if (section && name) {
      prefillChat(`Tell me more about ${name} — specifically ${section.toLowerCase()}`);
      return;
    }
    if (name) {
      prefillChat(buildTopicExplorePrompt(name));
    }
  }, [prefillChat, topic?.name]);

  const pageCopyPalette = useMemo(
    () => ({
      fg: accent,
      bg: `${accent}22`,
      soft: `${accent}0d`,
    }),
    [accent],
  );

  const sendTopicMessage = useCallback(async (formattedMessage) => {
    const text = String(formattedMessage?.text || '').trim();
    if (!text || isSending) return false;

    if (!(await socketManager.requireConnection())) {
      return false;
    }

    if (!isPlus && !hasAvailableCredits(totalAvailableCredits)) {
      openCreditsRecoveryModal(openModal, useAuthStore.getState().user, {
        source: 'topic_composer_send',
        title: 'Out of credits',
        subtitle: "You've used all your free credits. Upgrade to Plus for more monthly credits.",
      });
      return false;
    }

    setIsSending(true);
    try {
      const conversation = await createConversation({
        userId,
        formattedMessage: { ...formattedMessage, text },
      });
      if (!conversation?.id) {
        throw new Error('Could not start a conversation.');
      }

      // Kick off inference on the freshly-created conversation. createConversation
      // only persists the user message + schedules the title; without this the
      // thread would sit with no assistant reply (and no thinking indicator)
      // until a manual refresh. Mirrors app.jsx / g/[id] / m/[id] / collections.
      // emitKickConversation also flips the chat into the waiting state so the
      // indicator shows immediately after navigation.
      setTimeout(() => {
        socketManager.emitKickConversation(userId, conversation.id);
      }, 250);

      router.push(`/c/${conversation.id}`);
      return true;
    } catch (e) {
      addToast(e?.message || 'Could not start a conversation.', 'error');
      return false;
    } finally {
      setIsSending(false);
    }
  }, [
    isSending,
    isPlus,
    totalAvailableCredits,
    openModal,
    createConversation,
    userId,
    router,
    addToast,
  ]);

  const handleSend = useCallback(async (formattedMessage) => {
    if (!topic) return false;
    if (isAuthenticated) {
      return sendTopicMessage(formattedMessage);
    }
    requireAuth(
      () => { sendTopicMessage(formattedMessage); },
      {
        source: 'topic_start_composer',
        title: topic.name ? `Sign in to explore ${topic.name}` : 'Sign in to continue',
        subtitle: 'Create a free account to ask questions and save your research.',
        icon: 'book',
      },
    );
    return false;
  }, [topic, isAuthenticated, requireAuth, sendTopicMessage]);

  const exploreTopic = useCallback(async () => {
    if (!topic?.name || !composerContextId) return;
    const promptText = buildTopicExplorePrompt(topic.name);
    setInputContext(composerContextId);
    setText(promptText);

    const formattedMessage = {
      ...useChatInputStore.getState().getFormattedMessage(),
      text: promptText,
    };

    const accepted = await handleSend(formattedMessage);
    if (accepted === true) {
      useChatInputStore.getState().clearInput();
    }
  }, [topic?.name, composerContextId, setInputContext, setText, handleSend]);

  const openRelated = useCallback(
    (row) => {
      if (row?.slug) router.push(`/start/${encodeURIComponent(row.slug)}`);
    },
    [router],
  );

  const openTopicFeedback = useTopicPageFeedback(slug, topic?.name);

  const canonicalPath = `/start/${encodeURIComponent(slug || '')}`;
  const metaTitle =
    topic?.meta?.title ||
    (topic ? `${topic.name} · Gab AI` : t('pages.topics.title'));
  const metaDescription = useMemo(
    () => buildTopicSerpDescription({
      topic,
      pageCopyBlocks,
      fallback: t('pages.topics.seoMetaFallback'),
    }),
    [topic, pageCopyBlocks, t],
  );
  const shareImage = useMemo(() => resolveTopicShareImage(topic), [topic]);

  const convCount = topic?.stats?.conversationCount || 0;
  const topicTags = useMemo(
    () => (Array.isArray(topic?.tags) ? topic.tags : []).filter((tag) => tag?.slug && tag?.name),
    [topic?.tags],
  );

  const structuredData = useMemo(() => {
    if (!topic) return null;
    return buildTopicPageStructuredData({
      topic,
      metaTitle,
      metaDescription,
      canonicalPath,
      pageCopyBlocks,
      topicsHubName: t('pages.topics.title'),
    });
  }, [topic, metaTitle, metaDescription, canonicalPath, pageCopyBlocks, t]);

  if (!slug) {
    return (
      <View style={styles.container}>
        <View style={styles.notFound}>
          <Text type="h2">{t('pages.topics.missingSlug')}</Text>
        </View>
      </View>
    );
  }

  if (loadingRemote && !topic && !staticTopic) {
    return (
      <View style={[styles.container, styles.loadingBox]}>
        <MetaHead title={metaTitle} description={metaDescription} canonical={canonicalPath} />
        <ActivityIndicator size="large" color={accent} />
        <Text type="p" style={{ opacity: 0.7 }}>{t('pages.topics.loading')}</Text>
      </View>
    );
  }

  if (!topic && (loadError === 'notfound' || (!loadingRemote && !staticTopic))) {
    return (
      <View style={styles.container}>
        {/* This branch also catches fetch failures, not just missing topics, so
            noindex is gated on a confirmed miss. Emitting it on a transient
            error would ask Google to drop a topic page that still exists. */}
        <MetaHead
          noindex={loadError === 'notfound'}
          title={metaTitle}
          description={metaDescription}
          canonical={canonicalPath}
        />
        <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
          <View style={styles.inner}>
            <View style={styles.notFound}>
              <Text type="h2">{t('pages.topics.notFound')}</Text>
              <Text type="p" style={{ textAlign: 'center', opacity: 0.75 }}>
                {t('pages.topics.removedUnavailable')}
              </Text>
            </View>
          </View>
        </ScrollView>
      </View>
    );
  }

  if (!topic) {
    return (
      <View style={[styles.container, styles.loadingBox]}>
        <MetaHead title={metaTitle} description={metaDescription} canonical={canonicalPath} />
        <ActivityIndicator size="large" color={accent} />
      </View>
    );
  }

  const relatedTopics = relatedTopicRows;

  return (
    <View style={styles.container}>
      <MetaHead
        title={metaTitle}
        description={metaDescription}
        canonical={canonicalPath}
        keywords={topic.meta?.keywords}
        structuredData={structuredData}
        lastModified={topic.updatedAt}
        {...(shareImage ? { image: shareImage } : {})}
      />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={showsScrollIndicator}
        keyboardShouldPersistTaps="handled"
      >
        <View style={styles.fullBleedBand}>
          <CatalogDetailHero
            backLink={{
              label: t('pages.topics.browseLabel'),
              onPress: () => router.push('/topics'),
            }}
            categorySlug={categorySlug}
            catalogCategory={{
              label: topic.category?.name || t('pages.topics.title'),
              icon: 'book',
              href: categorySlug
                ? `/topics/category/${encodeURIComponent(categorySlug)}`
                : undefined,
            }}
            heroMaxWidth={1100}
            avatar={{ uri: topic.avatarPath || null }}
            headline={topic.name}
            headlineNativeId="topic-speakable-headline"
            subtitle={topic.description || null}
            subtitleNativeId="topic-speakable-summary"
            primaryAction={{
              label: t('pages.topics.exploreCta'),
              icon: 'comments',
              onPress: exploreTopic,
            }}
            metaItems={convCount > 0 ? [{
              icon: 'comments',
              label: `${formatCount(convCount)} ${t('pages.topics.conversationsLabel')}`,
            }] : []}
          />
        </View>

        <View style={styles.inner} nativeID="main-content">
          <View style={styles.contentSplit}>
            <View style={styles.pageCopyCol}>
              <View style={styles.explainerCard} {...snippetExcludeWebProps()}>
                <Text style={{ fontSize: 11, fontWeight: '800', letterSpacing: 1.2, opacity: 0.6 }}>
                  {t('pages.topics.guideEyebrow')}
                </Text>
                <Text type="h3">{t('pages.topics.guideTitle')}</Text>
                <Text type="p" style={{ opacity: 0.85 }}>
                  {t('pages.topics.guideBody')}
                </Text>
              </View>

              {pageCopyBlocks.length > 0 && (
                <TopicPageCopy
                  blocks={pageCopyBlocks}
                  modelName={composerModelName}
                  modelLogoPath={resolvedComposerModel.logoPath}
                  accent={accent}
                  palette={pageCopyPalette}
                  isMD={isSM || isMD}
                  onQuestionPress={handlePageCopyQuestion}
                  onContinuePress={handlePageCopyContinue}
                />
              )}
            </View>

            {prompts.length > 0 && (
              <View style={styles.promptsSidebar} {...snippetExcludeWebProps()}>
                <View style={styles.promptsSidebarCard}>
                  <Text size="xxxl" weight="bold" style={styles.starterSectionTitle}>
                    {t('pages.topics.starterQuestions')}
                  </Text>
                    <View style={styles.promptList}>
                      {prompts.map((p) => (
                        <Pressable
                          key={p}
                          style={({ pressed, hovered }) => [
                            styles.promptRow,
                            Platform.OS === 'web' && hovered && !pressed && styles.promptRowHovered,
                            pressed && { opacity: 0.88 },
                          ]}
                          onPress={() => prefillChat(buildDefaultPromptPrefill(p))}
                          accessibilityRole="button"
                        >
                          <Icon name="circle-question" size={16} color={accent} />
                          <Text type="p" size="s" style={{ flex: 1, lineHeight: 20 }}>{p}</Text>
                          <Icon name="arrow-right" size={14} color={colors.text_color_secondary} />
                        </Pressable>
                      ))}
                    </View>
                  </View>
                </View>
              )}
          </View>

          {relatedTopics.length > 0 && (
            <View {...snippetExcludeWebProps()}>
              <Text size="xxxl" weight="bold" style={styles.relatedSectionTitle}>
                {t('pages.topics.relatedTopics')}
              </Text>
              <View style={styles.relatedGrid}>
                {relatedTopics.map((row) => (
                  <RelatedTopicCard
                    key={row.id}
                    row={row}
                    styles={styles}
                    onPress={openRelated}
                  />
                ))}
              </View>
            </View>
          )}

          {topicTags.length > 0 && (
            <View style={styles.tagsSection} {...snippetExcludeWebProps()}>
              <Text size="xxxl" weight="bold" style={styles.tagsSectionTitle}>
                {t('pages.topics.relatedTags')}
              </Text>
              <TopicTagChips tags={topicTags} accent={accent} />
            </View>
          )}

          <TopicPageFeedback accent={accent} onPress={openTopicFeedback} />

          {topic?.name ? (
            <View
              style={{
                marginTop: 32,
                padding: isXS ? 24 : 32,
                borderRadius: 20,
                borderWidth: 1,
                borderColor: colors.border_color_secondary,
                backgroundColor: colors.solid_color_block,
                alignItems: 'center',
              }}
              {...snippetExcludeWebProps()}
            >
              <Text size="l" weight="bold" style={{ textAlign: 'center', marginBottom: 12 }}>
                Ask Gab AI about {topic.name}
              </Text>
              <Text color="secondary" style={{ textAlign: 'center', marginBottom: 20, lineHeight: 22 }}>
                Start a conversation grounded in this topic — free to try.
              </Text>
              <Link href="/signup" asChild>
                <Pressable
                  style={{
                    paddingHorizontal: 28,
                    paddingVertical: 14,
                    borderRadius: 999,
                    backgroundColor: accent,
                  }}
                >
                  <Text weight="bold" style={{ color: '#FFFFFF' }}>Try Gab AI free</Text>
                </Pressable>
              </Link>
            </View>
          ) : null}
        </View>

        <View style={styles.footerWrap} {...snippetExcludeWebProps()}>
          <AppDashboardFooter />
        </View>
      </ScrollView>

      <View style={styles.floatingComposer} {...snippetExcludeWebProps()}>
        <ChatInputContainer forceInputStyle={CHAT_INPUT_STYLE.FLOATING}>
          <ChatInput
            key={composerContextId}
            conversationId={composerContextId}
            forceInputStyle={CHAT_INPUT_STYLE.FLOATING}
            placeholder={chatPlaceholder}
            forcePlaceholder
            onSend={handleSend}
            isDisabled={isSending}
            isLoading={isSending}
            withModels={false}
            hideVoiceMode
            isCompact
          />
        </ChatInputContainer>
      </View>
    </View>
  );
}
